'use client'

import { ReactSelectMenu } from '@repo/ui/ReactSelectMenu'
import { useBankFeedsStore } from '@repo/store/useBankFeedsStore'
import { useMemo } from 'react'

export const PlaidBankAccountSelect = ({
  testData = null,
  value = null,
  onChange = (val) => {
    console.log(val)
  },
  sx,
  size = 'small',
  required,
  label
}: PlaidBankAccountSelectProps) => {
  const { bankFeeds, fetchAllBankFeeds } = useBankFeedsStore()

  const VALUE_KEY = 'plaidBankAccountId'

  const formatLabel = (x: Partial<BankFeed>) => {
    const name = x.ospreyName ?? ''
    const inst = x.institutionName ?? 'Unknown bank'
    const cur = x.isoCurrencyCode ?? '—'
    const mask = x.mask != null ? `****${String(x.mask)}` : '****'
    return `${name} (${inst}, ${cur}, ${mask})`
  }

  const toId = (val: any): string | null => {
    if (val == null) return null
    if (typeof val === 'string' || typeof val === 'number') return String(val)
    return val?.[VALUE_KEY] ? String(val[VALUE_KEY]) : null
  }

  // remove plaidRecipientId = null option since it's not a bank account
  const options = useMemo(() => {
    const source: BankFeed[] = (testData ?? bankFeeds ?? []) as BankFeed[]
    return source.map((x) => ({
      ...x,
      __label: formatLabel(x)
    }))
  }, [testData, bankFeeds])

  const resolvedValue = useMemo(() => {
    const id = toId(value)
    if (!id) return null
    return options.find((o) => String(o[VALUE_KEY]) === id) ?? null
  }, [value, options])

  const handleChange = (next: any) => {
    const id = toId(next)
    const obj = id ? (options.find((o) => String(o[VALUE_KEY]) === id) ?? next) : next
    onChange(obj)
    console.log('obj', obj)
  }

  return (
    <div data-testid="PlaidBankAccountSelect" style={{ width: sx?.width || '100%', maxWidth: sx?.maxWidth || '100%' }}>
      <ReactSelectMenu
        idLabelKey="name"
        items={options}
        label={label ?? 'Plaid bank account'}
        labelKey="__label"
        name="plaidBankAccounts"
        required={required}
        searchable
        showIdInResults={false}
        size={size}
        sx={{ [`& > div`]: { left: 0, width: '100%' } }}
        value={resolvedValue}
        onChange={handleChange}
        valueKey={VALUE_KEY}
      />
    </div>
  )
}

type PlaidBankAccountSelectProps = {
  testData?: any
  value?: any
  onChange?: (val: any) => void
  sx?: any
  size?: 'small' | 'medium'
  required?: boolean
  label?: string
}

type BankFeed = {
  accountType: string
  createdBy: string
  createdDate: string
  institutionName: string
  isoCurrencyCode: string
  mask: string
  name: string
  ospreyBankAccountId: string
  ospreyBankName: string
  ospreyName: string
  plaidBankAccountId: string
  plaidInstitutionId: string
  plaidRecipientId: string
  subType: string
  updatedBy: string
  updatedDate: string
}
